import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import useDetectOutsideClick from '../hooks/useDetectOutsideClick';
import Icon from '../components/common/Icon';
import { icons } from '../config/icons';

const PopupMenuContext = createContext();

export const usePopupMenu = () => useContext(PopupMenuContext);

const PopupMenu = ({ content, referenceElement, placement, paneProps, onClose }) => {
    const [popperElement, setPopperElement] = useState(null);
    const [mounted, setMounted] = useState(false);

    useEffect(() => { setMounted(true); }, []);

    //bottom-end, left-start, top-end, 
    const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
        placement: placement || 'bottom-end',
        modifiers: [
            { name: 'offset', options: { offset: [0, 8] } },
            { name: 'flip', options: { fallbackPlacements: ['top-end', 'left-start'] } },
            { name: 'preventOverflow', options: { padding: 10 } },
        ],
    });

    useEffect(() => { if (update) { update(); } }, [content, update]);
    useDetectOutsideClick({ current: popperElement }, onClose);

    return createPortal(
        <div
            ref={setPopperElement}
            style={{ ...styles.popper, opacity: mounted ? 1 : 0 }} // Initially invisible
            {...attributes.popper}
            className="popup-menu-container scrollbar-small"
        >
            {paneProps ? (
                <div className='popup-menu-pane' style={{ width: paneProps.width || '250px' }}>
                    <div className='popup-menu-pane-head'>
                        <span><h4>{paneProps.title}</h4></span>
                        <div className='popup-menu-pane-nav'>
                            <button onClick={onClose}><Icon icon={icons.close} /></button>
                        </div>
                    </div>
                    <div className='popup-menu-pane-body'>
                        <div className='popup-menu-pane-grid'>
                            {content}
                        </div>
                    </div>
                </div>
            ) : (
                content
            )}
        </div>,
        document.body
    );
};

export const PopupMenuProvider = ({ children }) => {
    const [menuState, setMenuState] = useState({
        isOpen: false,
        referenceElement: null,
        content: null,
        placement: 'bottom-end',
        paneProps: null,
    });

    const openPopupMenu = useCallback(({ referenceElement, content, placement = 'bottom-end', paneProps = null }) => {
        setMenuState({
            isOpen: true,
            referenceElement,
            content,
            placement,
            paneProps,
        });
    }, []);

    const closePopupMenu = useCallback(() => {
        setMenuState(prev => ({ ...prev, isOpen: false, referenceElement: null }));
    }, []);

    const value = useMemo(() => ({
        openPopupMenu,
        closePopupMenu
    }), [openPopupMenu, closePopupMenu]);

    return (
        <PopupMenuContext.Provider value={value}>
            {children}
            {menuState.isOpen && (
                <PopupMenu
                    referenceElement={menuState.referenceElement}
                    content={menuState.content}
                    placement={menuState.placement}
                    paneProps={menuState.paneProps}
                    onClose={closePopupMenu}
                />
            )}
        </PopupMenuContext.Provider>
    );
};